import React, { useState, useEffect } from 'react';
import { validateApiKey, storeUserData, consumeLogoutReason } from '../../utils/apiKeyAuth';
import { consumeReturnTo } from '../../utils/returnTo';
import styles from './Login.module.scss';

const Login = () => {
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Show why the user was signed out (deactivated account/company, expired key).
  useEffect(() => {
    const reason = consumeLogoutReason();
    if (reason) setNotice(reason);
  }, []);

  const handleChange = (e) => {
    setApiKey(e.target.value);
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate API key is provided
      if (!apiKey.trim()) {
        throw new Error('Please enter your API key');
      }

      // Call the validation API
      const result = await validateApiKey(apiKey.trim());

      // Check if validation was successful
      if (!result.is_success) {
        throw new Error(result.error || 'Invalid API key');
      }

      // Store validated user data
      const stored = storeUserData(result);
      if (!stored) {
        throw new Error('Failed to store user data. Please try again.');
      }

      console.log('Authentication successful:', result);

      // Redirect to home page (or back to the Claude/Town page that sent the
      // user here) - using window.location to ensure UserContext reinitializes
      window.location.href = consumeReturnTo() || '/';

    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An error occurred during authentication. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginBox}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}>A</div>
        </div>

        <h1 className={styles.title}>Welcome to Agent Studio</h1>
        <p className={styles.subtitle}>Enter your API key to continue</p>

        {notice && (
          <div
            className={styles.error}
            role="status"
            style={{ marginBottom: 16 }}
          >
            {notice}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="apiKey" className={styles.label}>
              API Key
            </label>
            <div className={styles.inputWrapper}>
              <input
                type={showApiKey ? "text" : "password"}
                id="apiKey"
                name="apiKey"
                value={apiKey}
                onChange={handleChange}
                required
                className={styles.input}
                placeholder="Enter your API key"
                disabled={loading}
                autoComplete="off"
              />
              <button
                type="button"
                className={styles.toggleButton}
                onClick={() => setShowApiKey(!showApiKey)}
                disabled={loading}
                aria-label={showApiKey ? "Hide API key" : "Show API key"}
              >
                {showApiKey ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? (
              <span className={styles.buttonContent}>
                <span className={styles.spinner}></span>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <p>Authentication is required to access Agent Studio</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
